'use client';

import React, { memo, useCallback, useMemo, useState } from 'react';
import { AddIcon } from '@/_assets/icon-components';
import { Box, Button, Card, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { Link, useRouter, usePathname } from '@/i18n/navigation';
import CardHeader from './components/CardHeader';
import { ICategory, IDiscount } from '@/_types';
import { IRedux } from '@/_store/types';
import { useDispatch, useSelector } from 'react-redux';
import AddToCart from '@/_components/AddToCart';
import useCartProduct from '@/_hooks/useCartProduct';
import NumberToPrice from '@/_components/NumberToPrice';
import ConfirmModal from '@/_components/ConfirmModal';
import { onOpenMap } from '@/_store/common/common.slice';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import {
  ADD_TO_CART,
  REMOVE,
  REMOVE_PRODUCTS_BY_OPPOSITE_RESTRICTION,
  SET_QUANTITY,
} from '@/_store/cart/cart.slice';
import useGTM from '@/_hooks/useGTM';
import { track } from '@/_utils/utm';
import { useTelegram } from '@/_providers/telegram';
import { useActiveBrand } from '../../_hooks/useActiveBrand';
import { IProduct } from '@/entities/product/model/types';
import { BRAND_NAMES } from '../../_utils/Constants';
import { needsDestinationSelection } from '@/_lib/destination-gate';
import { getProductHref } from '@/_lib/product-href';
import { getBrandHref } from '@/_lib/brand-href';

const EMPTY_CART_PRODUCTS: never[] = [];

const ProductCard = ({
  product,
  category,
  brandId: brandIdProp,
}: {
  product: Partial<IProduct>;
  discounts?: IDiscount[];
  category: ICategory | null;
  brandId?: string;
}) => {
  const currentLocale = useCustomLocale();
  const dispatch = useDispatch();
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();

  const { sendGTM } = useGTM();
  const shipperId = useActiveBrand();
  const { tg_chat_id } = useTelegram();
  const { quantity, productInCart } = useCartProduct(product?.id);
  const { branches } = useSelector((state: IRedux) => state.common);
  const { data, country } = useSelector((state: IRedux) => state.settings);
  const cartProducts = useSelector(
    (state: IRedux) =>
      state.cart.cart?.[shipperId]?.products ?? EMPTY_CART_PRODUCTS,
  );
  const [showRestrictedConfirm, setShowRestrictedConfirm] = useState(false);

  const branch = branches?.[shipperId];

  const { permissions, is_hall, menu_id, order_type } = useSelector(
    (state: IRedux) => state.session,
  );

  const totalDiscount = useMemo(() => {
    let result = 0;
    if (product?.discounts?.length) {
      result = product?.discounts?.reduce((acc, cur) => {
        return acc + cur?.discount_price;
      }, 0);
    }

    return result;
  }, [product.discounts]);

  // Extract brand id from pathname if on brand page, or use prop
  const brandId = useMemo(() => {
    if (brandIdProp) return brandIdProp;
    const brandMatch = pathname?.match(/\/brand\/([^\/]+)/);
    return brandMatch ? brandMatch[1] : null;
  }, [pathname, brandIdProp]);

  const productIdentifier = product.slug || product.id;
  const productHref = getProductHref(productIdentifier, brandId);

  const navigateTo = useCallback(() => {
    sendGTM('select_item', { product, name: data?.data?.shipper_name });
    router.push(productHref);
  }, [
    product,
    productHref,
    data?.data?.shipper_name,
    sendGTM,
    router,
  ]);

  const onProductLinkClick = useCallback(() => {
    sendGTM('select_item', { product, name: data?.data?.shipper_name });
  }, [product, data?.data?.shipper_name, sendGTM]);

  const navigateToRestaurant = () => {
    if (
      needsDestinationSelection({
        isHall: is_hall,
        orderType: order_type?.type,
        branchId: branch?.id,
        geolocation: permissions.geolocation,
      })
    ) {
      dispatch(onOpenMap());
      return;
    }

    sendGTM('select_item', { product, name: data?.data?.shipper_name });

    // Use brand route with optional category hash for scroll-to-section
    const route = getBrandHref(data?.shipper_id, {
      hash: category?.slug || undefined,
    });
    router.push(route);
  };

  const addToCartHandler = useCallback(() => {
    if (
      product.type === 'origin' ||
      !product?.out_price ||
      !product.title ||
      !product.id ||
      !product.type
    )
      return;

    dispatch(
      ADD_TO_CART({
        image: product?.image || '',
        brand: BRAND_NAMES?.[shipperId],
        key: '',
        comment: '',
        in_stop: false,
        default_product: false,
        category_name: category?.title?.[currentLocale],
        category_id: category?.id,
        category_is_restricted: category?.is_shopping_cart_restricted,
        discount_percent:
          totalDiscount !== 0
            ? Math.floor((totalDiscount / product?.out_price) * 100)
            : 0,
        product_name: product.title?.[currentLocale],
        price_with_discount: product.out_price + totalDiscount,
        price: product.out_price,
        product_id: product.id,
        type: product.type,
        variants: {},
        quantity: 1,
        order_modifiers: [],

        shipper_id: shipperId || '',
        source_id: '',
        branch_id: branch?.id || '',
        menu_id: branch?.menu_id || '',
      }),
    );
    track({
      action_type: 'add_to_cart',
      tg_chat_id,
    });
    sendGTM('add_to_cart', { product, quantity });
    sendGTM('add_to_cart_ads', { product, currency: country?.currency });
    sendGTM('add_to_cart_fb', { product, currency: country?.currency });

    if (navigator.vibrate) {
      navigator.vibrate(100);
    } else {
      console.warn('Vibration API is not supported on this device.');
    }
  }, [
    product,
    category,
    dispatch,
    totalDiscount,
    currentLocale,
    shipperId,
    branch,
  ]);

  const canAddToCartDirectly =
    product.type === 'simple' &&
    !product.has_modifier &&
    ((
      is_hall === 'true' ? (menu_id !== 'null' ? menu_id : '') : branch?.menu_id
    )
      ? product?.active_in_menu
      : true);

  const newProductRestricted = category?.is_shopping_cart_restricted ?? false;
  const hasOppositeCategoryInCart = useMemo(
    () =>
      cartProducts.some(
        (p) => (p.category_is_restricted ?? false) !== newProductRestricted,
      ),
    [cartProducts, newProductRestricted],
  );

  const onConfirmReplaceCart = useCallback(() => {
    dispatch(
      REMOVE_PRODUCTS_BY_OPPOSITE_RESTRICTION({
        shipper_id: shipperId,
        is_restricted: newProductRestricted,
      }),
    );
    setShowRestrictedConfirm(false);
    addToCartHandler();
  }, [dispatch, shipperId, newProductRestricted, addToCartHandler]);

  const onAddClick = useCallback(() => {
    if (
      needsDestinationSelection({
        isHall: is_hall,
        orderType: order_type?.type,
        branchId: branch?.id,
        geolocation: permissions.geolocation,
      })
    ) {
      dispatch(onOpenMap());
      return;
    }

    if (!canAddToCartDirectly) {
      navigateTo();
      return;
    }

    if (hasOppositeCategoryInCart) {
      setShowRestrictedConfirm(true);
      return;
    }

    addToCartHandler();
  }, [
    addToCartHandler,
    navigateTo,
    canAddToCartDirectly,
    hasOppositeCategoryInCart,
    permissions.geolocation,
    order_type?.type,
    branch?.id,
    is_hall,
    dispatch,
  ]);

  const removeHandler = () => {
    if (productInCart) {
      sendGTM('remove_from_cart', {
        cart: [productInCart],
        name: data?.data?.shipper_name,
      });
      dispatch(REMOVE({ key: productInCart?.key, shipper_id: shipperId }));
    }
  };

  const isActiveProduct =
    is_hall === 'true'
      ? (menu_id !== 'null' && Boolean(product.active_in_menu)) ||
        menu_id == 'null'
      : (branch?.menu_id && product.active_in_menu) || !branch?.menu_id;

  return (
    <Card.Root
      rounded={16}
      cursor={'pointer'}
      border='1px solid #F4F4F4'
      boxShadow='0px 4px 20px 0px #0000000F'
    >
      <Link
        href={productHref}
        onClick={onProductLinkClick}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <Card.Header px={{ base: 2, lg: 4 }} pt={{ base: 2, lg: 4 }}>
          <CardHeader totalDiscount={totalDiscount} product={product} />
        </Card.Header>
        <Card.Body
          pb={3}
          px={{ base: 2, lg: 4 }}
          minH='78px'
          position='relative'
          pt={{ base: 3, lg: 4 }}
        >
          <NumberToPrice
            textProps={{
              fontSize: { lg: 'xl' },
              lineHeight: 1,
              fontWeight: 600,
              color: 'price',
            }}
            spanProps={{
              fontSize: { lg: 'xl' },
              lineHeight: 1,
              fontWeight: 600,
              color: 'price',
            }}
            value={(product?.out_price || 0) + totalDiscount}
          />
          {totalDiscount !== 0 && (
            <Box
              textDecor={'line-through'}
              fontSize={{ base: 'xs', lg: 'sm' }}
              color='text.muted'
              mt={1}
            >
              <NumberToPrice
                textProps={{
                  lineHeight: 1,
                  fontWeight: 600,
                }}
                spanProps={{
                  lineHeight: 1,
                  fontWeight: 600,
                }}
                value={product?.out_price || 0}
              />
            </Box>
          )}
          <Text
            fontSize={{ base: 'sm', lg: 'md' }}
            height={{ base: 'auto', lg: 10 }}
            lineHeight={1.2}
            fontWeight={500}
            lineClamp={2}
            color='text'
            mt={1}
          >
            {product?.title?.[currentLocale]}
          </Text>
        </Card.Body>
      </Link>
      <Card.Footer
        px={{ base: 2, lg: 4 }}
        pb={{ base: 2, lg: 4 }}
        flexDir={'column'}
        justifyContent={'flex-start'}
        alignItems={'start'}
        color='product_button.text'
      >
        {!category?.is_shopping_cart_restricted &&
        category?.is_show_in_main_page ? (
          <Button
            w={'full'}
            rounded={12}
            variant='subtle'
            fontWeight={600}
            height={{ lg: 12 }}
            onClick={navigateToRestaurant}
            size={{ base: 'md', lg: 'lg' }}
            bg='product_button'
            color='product_button.text'
            _hover={{
              bgColor: 'product_button.hover',
            }}
            disabled={!isActiveProduct}
          >
            {isActiveProduct ? (
              <>
                <AddIcon color='currentColor' />
                <Text as='span' fontWeight={600}>
                  {t('add_to_cart')}
                </Text>
              </>
            ) : (
              <Text as='span' fontWeight={500}>
                {t('will_be_later')}
              </Text>
            )}
          </Button>
        ) : (
          <AddToCart
            onClick={onAddClick}
            quantity={quantity}
            isDisabled={!isActiveProduct}
            isOrdered={Boolean(productInCart)}
            onRemove={() => removeHandler()}
            setQuantity={(e: number) => {
              if (!productInCart?.key) return;
              dispatch(
                SET_QUANTITY({
                  key: productInCart?.key,
                  quantity: e,
                  shipper_id: shipperId,
                }),
              );
            }}
          >
            {isActiveProduct ? (
              <>
                <AddIcon color='currentColor' />
                <Text as='span' fontWeight={600}>
                  {t('add_to_cart')}
                </Text>
              </>
            ) : (
              <Text as='span' fontWeight={500}>
                {t('will_be_later')}
              </Text>
            )}
          </AddToCart>
        )}
      </Card.Footer>
      <ConfirmModal
        isOpen={showRestrictedConfirm}
        onClose={() => setShowRestrictedConfirm(false)}
        onConfirm={onConfirmReplaceCart}
        title={t('attention')}
        description={t('cart_restricted_confirm_message')}
        text={{ confirm: 'yes', cancel: 'no' }}
      />
    </Card.Root>
  );
};

export default memo(ProductCard);
